import type { ClipboardEvent, KeyboardEvent } from "react";

export const ROLE_LABELS = {
    ROLE_ADMIN: "Administrator",
    ROLE_USER: "User",
    ROLE_COMPANY_ADMIN: "Company Admin",
    ROLE_COACH: "Coach"
};

type Role = keyof typeof ROLE_LABELS;

export const getRoleLabel = (role: Role): string => ROLE_LABELS[role] || "Unknown Role";

export const formatRoles = (roles: Role[]): string => roles.map(getRoleLabel).join(", ");
 
export const selectDataConverter = (
    data: any,
    labelKey: string,
    valueKey: string
  ): { label: string; value: any }[] => {
    if (!data || typeof data !== "object") {
      return [];
    }
  
    return Object.keys(data).map((key) => ({
      label: data[key][labelKey],
      value: data[key][valueKey],
    }));
  };


 export const formatDateToYYYYMMDDLocal =(date: Date) => {
    const year = date?.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Ensure 2 digits
    const day = String(date.getDate()).padStart(2, '0'); // Ensure 2 digits
    return `${year}-${month}-${day}`;
  }
  

  export const mapBackendFiles = (documents: any[] | any) => {
    if (Array.isArray(documents)) {
      // If it's an array, map each document
      return documents.map((doc) => ({
        id: doc.id,
        path: doc.path,
        name: doc.file?.name || "Unknown",
        mime: doc.file?.mime || "application/octet-stream",
        nameOriginal: doc.file?.nameOriginal || "Unknown File",
        isExisting: true, // Mark as an existing file
        file: null, // No file object for existing files
      }));
    } else if (typeof documents === "object" && documents !== null) {
      console.log(documents,'documenta')
      // If it's a single object, wrap it in an array
      return {
          id: documents.id,
          path: documents.path,
          name: documents.file?.name || "Unknown",
          mime: documents.file?.mime || "application/octet-stream",
          nameOriginal: documents.file?.nameOriginal || "Unknown File",
          isExisting: true,
          file: null,
        }
      
    } else {
      // If it's neither an array nor an object, return an empty array
      return [];
    }
  };

// Format number with German locale (dot for thousands, comma for decimals)
export const formatGermanNumber = (value: number | string | undefined | null): string => {
  if (value === undefined || value === null || value === "") return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "-";
  return num.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Keep a minus sign from ever reaching an input that must not go below zero.
// The keystroke and the paste are dropped, so nothing happens when it is tried.
export const blockNegativeKey = (event: KeyboardEvent<HTMLInputElement>) => {
  if (event.key === "-") {
    event.preventDefault();
  }
};

export const blockNegativePaste = (event: ClipboardEvent<HTMLInputElement>) => {
  if (event.clipboardData.getData("text").includes("-")) {
    event.preventDefault();
  }
};

// Backstop for the ways a negative value can still arrive (autofill, spinners,
// browsers that report a minus as an empty value first)
export const isNegativeInput = (value: string): boolean =>
  value.trim().startsWith("-") || Number(value) < 0;

// Limit decimal input to 2 places
export const limitToTwoDecimals = (value: string): string => {
  // Allow empty string
  if (value === "") return "";
  
  // Remove any non-numeric characters except dot and comma
  let cleaned = value.replace(/[^\d.,]/g, "");
  
  // Replace comma with dot for processing
  cleaned = cleaned.replace(",", ".");
  
  // Only allow one decimal point
  const parts = cleaned.split(".");
  if (parts.length > 2) {
    cleaned = parts[0] + "." + parts.slice(1).join("");
  }
  
  // Limit to 2 decimal places
  if (parts.length === 2 && parts[1].length > 2) {
    cleaned = parts[0] + "." + parts[1].substring(0, 2);
  }
  
  return cleaned;
};